/**
 * Wargaming View
 * 
 * Multi-turn adversarial simulation interface.
 */

import React, { useState } from 'react';
import { Crosshair, Play, Loader2, Users, Clock, Swords } from 'lucide-react';

export default function Wargaming({ apiUrl }) {
  const [config, setConfig] = useState({
    scenarioName: 'Taiwan Strait Crisis',
    actors: ['USA', 'China'],
    turns: 10
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [currentTurn, setCurrentTurn] = useState(0);

  const actorOptions = ['USA', 'China', 'Russia', 'Japan', 'Taiwan', 'South Korea', 'India', 'NATO'];

  async function runWargame() {
    setLoading(true);
    try {
      const response = await fetch(`${apiUrl}/wargame`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          scenario: { name: config.scenarioName },
          actors: config.actors,
          turns: config.turns
        })
      });
      const data = await response.json();
      setResult(data);
      setCurrentTurn(0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  function toggleActor(actor) {
    if (config.actors.includes(actor)) {
      setConfig({ ...config, actors: config.actors.filter(a => a !== actor) });
    } else {
      setConfig({ ...config, actors: [...config.actors, actor] });
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Wargaming</h1>
        <p className="text-slate-400">Multi-turn adversarial simulation</p>
      </div>

      {/* Configuration */}
      <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
        <h2 className="text-lg font-semibold mb-4">Configure Wargame</h2>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-slate-400 mb-2">Scenario Name</label>
            <input
              type="text"
              value={config.scenarioName}
              onChange={(e) => setConfig({ ...config, scenarioName: e.target.value })}
              className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2"
            />
          </div>

          <div>
            <label className="block text-sm text-slate-400 mb-2">
              <Users className="w-4 h-4 inline mr-1" />
              Actors ({config.actors.length} selected)
            </label>
            <div className="flex flex-wrap gap-2">
              {actorOptions.map(actor => (
                <button
                  key={actor}
                  onClick={() => toggleActor(actor)}
                  className={`px-3 py-2 rounded-lg border transition-colors ${
                    config.actors.includes(actor)
                      ? 'border-cyan-500 bg-cyan-500/20 text-cyan-400'
                      : 'border-slate-600 hover:border-slate-500'
                  }`}
                >
                  {actor}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm text-slate-400 mb-2">
              <Clock className="w-4 h-4 inline mr-1" />
              Number of Turns
            </label>
            <input
              type="range"
              min="5"
              max="30"
              value={config.turns}
              onChange={(e) => setConfig({ ...config, turns: parseInt(e.target.value) })}
              className="w-full"
            />
            <div className="text-center text-cyan-400">{config.turns} turns</div>
          </div>

          <button
            onClick={runWargame}
            disabled={loading || config.actors.length < 2}
            className="w-full px-6 py-3 bg-red-600 hover:bg-red-500 rounded-lg font-medium
                     disabled:opacity-50 flex items-center justify-center"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
            ) : (
              <Crosshair className="w-5 h-5 mr-2" />
            )}
            Launch Wargame
          </button>
        </div>
      </div>

      {/* Results */}
      {result && (
        <div className="space-y-6">
          {/* Summary */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h3 className="text-lg font-semibold mb-4">Wargame Results</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                <div className="text-sm text-slate-400">Turns Played</div>
                <div className="text-2xl font-bold text-cyan-400">
                  {result.turns?.length || 0}
                </div>
              </div>
              <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                <div className="text-sm text-slate-400">Actors</div>
                <div className="text-2xl font-bold text-cyan-400">
                  {result.actors?.length || 0}
                </div>
              </div>
              <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                <div className="text-sm text-slate-400">Escalation Trend</div>
                <div className={`text-xl font-bold ${
                  result.analysis?.escalationTrend === 'escalating' ? 'text-red-400' :
                  result.analysis?.escalationTrend === 'de-escalating' ? 'text-green-400' :
                  'text-yellow-400'
                }`}>
                  {result.analysis?.escalationTrend || 'N/A'}
                </div>
              </div>
              <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                <div className="text-sm text-slate-400">Pivotal Turns</div>
                <div className="text-2xl font-bold text-purple-400">
                  {result.analysis?.pivotalTurns?.length || 0}
                </div>
              </div>
            </div>

            {/* Turn Navigator */}
            {result.turns?.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <button
                    onClick={() => setCurrentTurn(Math.max(0, currentTurn - 1))}
                    disabled={currentTurn === 0}
                    className="px-4 py-2 bg-slate-700 rounded-lg disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <span className="text-lg font-semibold">
                    Turn {currentTurn + 1} of {result.turns.length}
                  </span>
                  <button
                    onClick={() => setCurrentTurn(Math.min(result.turns.length - 1, currentTurn + 1))}
                    disabled={currentTurn >= result.turns.length - 1}
                    className="px-4 py-2 bg-slate-700 rounded-lg disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>

                {/* Current Turn Details */}
                <div className="bg-slate-900 rounded-lg p-4">
                  <h4 className="font-semibold mb-3 flex items-center">
                    <Swords className="w-5 h-5 mr-2 text-red-400" />
                    Turn {currentTurn + 1} Actions
                  </h4>
                  <div className="space-y-2">
                    {result.turns[currentTurn]?.actions?.map((action, i) => (
                      <div key={i} className="flex items-center p-2 bg-slate-800 rounded">
                        <span className="font-medium text-cyan-400 w-24">{action.actor}</span>
                        <span className="text-slate-300">
                          {action.action?.type || JSON.stringify(action.action)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Raw Data */}
          <details className="bg-slate-800 rounded-xl border border-slate-700">
            <summary className="p-4 cursor-pointer hover:bg-slate-700/50">
              View Full Wargame Data
            </summary>
            <pre className="p-4 text-xs overflow-auto max-h-96 bg-slate-900">
              {JSON.stringify(result, null, 2)}
            </pre>
          </details>
        </div>
      )}
    </div>
  );
}
